// =====================================================
// TEMPLATE LOADER MODAL - Cargar plantilla de presupuesto
// FIX P3: onLoad ahora pasa el flag replaceExisting para que
// BudgetFormPage pueda gestionar el modo añadir vs reemplazar.
// =====================================================
import { useState, useEffect } from 'react';
import { X, FileStack, Loader2, ChevronRight, AlertTriangle, Check } from 'lucide-react';
import { templatesApi, type TemplateSelect, type TemplatePreview } from '../../api/templates';

interface TemplateLoaderModalProps {
  isOpen: boolean;
  onClose: () => void;
  // FIX P3: Incluir replaceExisting en el callback para que el padre
  // pueda decidir si reemplaza o añade/mezcla con las líneas existentes.
  onLoad: (template: TemplatePreview, replaceExisting: boolean) => void;
  siteId: string;
  hasExistingLines: boolean;
}

export function TemplateLoaderModal({
  isOpen,
  onClose,
  onLoad,
  siteId,
  hasExistingLines,
}: TemplateLoaderModalProps) {
  const [templates, setTemplates] = useState<TemplateSelect[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedTemplate, setSelectedTemplate] = useState<TemplateSelect | null>(null);
  const [preview, setPreview] = useState<TemplatePreview | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Cuando hay líneas existentes, el defecto es reemplazar.
  // Si no las hay, este flag no aplica pero se deja en false.
  const [replaceExisting, setReplaceExisting] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen, siteId]);

  // Resetear opción al cambiar plantilla o al abrir el modal
  useEffect(() => {
    setReplaceExisting(true);
  }, [isOpen, selectedTemplate]);

  const loadTemplates = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await templatesApi.getForSelect(siteId);
      setTemplates(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar las plantillas');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectTemplate = async (template: TemplateSelect) => {
    setSelectedTemplate(template);
    setIsLoadingPreview(true);
    setError(null);
    try {
      const data = await templatesApi.getPreview(template.id, siteId);
      setPreview(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar la vista previa');
      setPreview(null);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleConfirmLoad = () => {
    if (!preview) return;
    // FIX P3: Pasar replaceExisting al callback.
    // Si no hay líneas existentes, siempre es "añadir" (replaceExisting = false).
    onLoad(preview, hasExistingLines ? replaceExisting : false);
  };

  const handleClose = () => {
    setSelectedTemplate(null);
    setPreview(null);
    setError(null);
    setReplaceExisting(true);
    onClose();
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={handleClose} />

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <FileStack className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Cargar plantilla</h3>
                <p className="text-sm text-gray-500">
                  Selecciona una plantilla para añadir actos al presupuesto
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 flex overflow-hidden">
            {/* Lista de plantillas */}
            <div className="w-1/3 border-r border-gray-200 overflow-y-auto">
              <div className="p-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Plantillas disponibles
                </h4>

                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  </div>
                ) : templates.length > 0 ? (
                  <div className="space-y-2">
                    {templates.map((template) => (
                      <button
                        key={template.id}
                        onClick={() => handleSelectTemplate(template)}
                        className={`w-full flex items-center justify-between p-3 rounded-xl text-left transition-all ${
                          selectedTemplate?.id === template.id
                            ? 'bg-purple-50 border-2 border-purple-300'
                            : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                        }`}
                      >
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 truncate">
                            {template.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {template.code} • {template.itemsCount} actos
                          </div>
                        </div>
                        <ChevronRight
                          className={`h-5 w-5 flex-shrink-0 transition-colors ${
                            selectedTemplate?.id === template.id
                              ? 'text-purple-500'
                              : 'text-gray-400'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FileStack className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No hay plantillas disponibles</p>
                  </div>
                )}
              </div>
            </div>

            {/* Vista previa */}
            <div className="flex-1 overflow-y-auto">
              {isLoadingPreview ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                </div>
              ) : preview ? (
                <div className="p-6">
                  <div className="mb-4">
                    <h4 className="text-lg font-semibold text-gray-900">
                      {preview.templateName}
                    </h4>
                    <p className="text-sm text-gray-500">
                      {preview.totalItems} actos • Total estimado: {formatPrice(preview.subtotal)}
                    </p>
                  </div>

                  {/* Líneas de la plantilla */}
                  <div className="space-y-2 mb-6">
                    {preview.lines.map((line, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono text-gray-500 bg-white px-1.5 py-0.5 rounded">
                              {line.medicalActCode}
                            </span>
                            <span className="text-sm font-medium text-gray-900 truncate">
                              {line.medicalActName}
                            </span>
                          </div>
                          {(line.tariffName || line.societyName) && (
                            <div className="text-xs text-gray-500 mt-0.5">
                              {line.tariffName && <span>Tarifa: {line.tariffName}</span>}
                              {line.tariffName && line.societyName && <span className="mx-1">•</span>}
                              {line.societyName && <span>Sociedad: {line.societyName}</span>}
                            </div>
                          )}
                        </div>
                        <div className="text-right ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {formatPrice(line.unitPrice)}
                          </div>
                          <div className="text-xs text-gray-500">
                            x{line.quantity}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Advertencia si hay líneas existentes */}
                  {hasExistingLines && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-amber-800">
                            El presupuesto ya tiene actos
                          </p>
                          <p className="text-sm text-amber-700 mt-1">
                            ¿Qué deseas hacer con los actos existentes?
                          </p>
                          <div className="mt-3 space-y-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="replaceOption"
                                checked={replaceExisting}
                                onChange={() => setReplaceExisting(true)}
                                className="text-purple-600 focus:ring-purple-500"
                              />
                              <span className="text-sm text-gray-700">
                                Reemplazar todos los actos existentes
                              </span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="replaceOption"
                                checked={!replaceExisting}
                                onChange={() => setReplaceExisting(false)}
                                className="text-purple-600 focus:ring-purple-500"
                              />
                              <span className="text-sm text-gray-700">
                                Añadir a los actos existentes
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Resumen y botón de carga */}
                  <div className="bg-purple-50 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm text-gray-600">Total estimado</div>
                        <div className="text-2xl font-bold text-purple-600">
                          {formatPrice(preview.subtotal)}
                        </div>
                      </div>
                      <button
                        onClick={handleConfirmLoad}
                        className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white font-semibold rounded-xl hover:bg-purple-700 transition-colors shadow-sm"
                      >
                        <Check className="h-5 w-5" />
                        {hasExistingLines
                          ? replaceExisting ? 'Reemplazar y cargar' : 'Añadir al presupuesto'
                          : 'Cargar plantilla'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <div className="text-center">
                    <FileStack className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="font-medium">Selecciona una plantilla</p>
                    <p className="text-sm mt-1">
                      Verás la vista previa aquí
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="px-6 py-3 bg-red-50 border-t border-red-200 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
